import { MagnifyingGlassIcon, MapPinIcon } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { Control, ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { formInputClassName } from "../../../styles/input";
import { cn } from "../../../utils";
import { InputCombobox, SearchElement } from "../InputCombobox";
import { FormControl, FormField, FormItem, FormLabel } from "./Form";

type FormSearchAddressProps<TForm extends FieldValues> = {
  control: Control<TForm>;
  name: Path<TForm>;
  label?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  onPlaceFind?: (place: google.maps.places.PlaceResult) => boolean | void;
  isMapApiLoaded: boolean;
  setValue?: (value?: string) => void;
  disabled?: boolean;
  setAddress?: (value: string) => void; // Outside setter, not related to this component value
  setLocationFilter?: (zipCode: string | null, city: string | null, country: string | null) => void;
  setLocationAndFilter?: (
    location: { lat: number; lng: number } | null,
    locationText: string,
    zipCode: string | null,
    city: string | null,
    country: string | null,
  ) => void;
  inputDecorator?: React.ReactNode;
  afterInputDecorator?: React.ReactNode;
};

export function FormSearchAddress<TForm extends FieldValues>({
  control,
  isMapApiLoaded,
  name,
  label,
  placeholder,
  className,
  inputClassName,
  onPlaceFind,
  setValue,
  disabled,
  setAddress,
  setLocationFilter,
  setLocationAndFilter,
  inputDecorator,
  afterInputDecorator,
}: FormSearchAddressProps<TForm>) {
  const autoCompleteRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placeServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);

  useEffect(() => {
    if (isMapApiLoaded) {
      if (!autoCompleteRef.current) {
        autoCompleteRef.current = new google.maps.places.AutocompleteService();
      }
      if (!placeServiceRef.current) {
        const map = new google.maps.Map(document.createElement("div"));
        placeServiceRef.current = new google.maps.places.PlacesService(map);
      }
    }
  }, [isMapApiLoaded]);

  /**
   * Handles the change of the search element
   * There are two cases:
   * - If the user selects a place from the autocomplete, the place details are fetched and the address is updated
   * - If the user types in the input, the address is updated with null coordinates
   */
  const handleChange = (
    { label, value: placeId }: SearchElement<string>,
    field: ControllerRenderProps<TForm, Path<TForm>>,
  ) => {
    if (placeId && placeServiceRef.current) {
      placeServiceRef.current.getDetails({ placeId }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          if (onPlaceFind && onPlaceFind(place) === false) return;
          const zipCode = place.address_components?.find((component) =>
            component.types.includes("postal_code"),
          )?.long_name;
          const city = place.address_components?.find((component) =>
            component.types.includes("locality"),
          )?.long_name;
          const country = place.address_components?.find((component) =>
            component.types.includes("country"),
          )?.long_name;

          const lat = place.geometry?.location?.lat() ?? null;
          const lng = place.geometry?.location?.lng() ?? null;
          const formattedAddress = place.formatted_address;

          setValue?.(formattedAddress);

          // Use atomic update if available, otherwise fall back to separate calls
          if (setLocationAndFilter && lat && lng && formattedAddress) {
            setLocationAndFilter(
              { lat, lng },
              formattedAddress,
              zipCode ?? null,
              city ?? null,
              country ?? null,
            );
          } else {
            setLocationFilter?.(zipCode ?? null, city ?? null, country ?? null);
          }

          field.onChange({
            address: formattedAddress,
            lat,
            long: lng,
            zipCode,
            city,
            country,
          });
        }
      });
    } else {
      field.onChange({
        address: label,
        lat: null,
        long: null,
        zipCode: null,
        city: null,
        country: null,
      });

      setLocationFilter?.(null, null, null);
    }
    // update suggestions
    if (autoCompleteRef.current) {
      autoCompleteRef.current.getPlacePredictions({ input: label }, (suggestions, status) => {
        if (suggestions && status === google.maps.places.PlacesServiceStatus.OK) {
          setSuggestions(suggestions);
        }
      });
    }

    setAddress?.(label);
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={cn(formInputClassName, className)}>
          {label && <FormLabel>{label}</FormLabel>}

          <FormControl>
            <InputCombobox
              className={inputClassName}
              elementsDecorator={<MapPinIcon size={20} className="text-primary-500 shrink-0" />}
              inputDecorator={
                inputDecorator ? (
                  inputDecorator
                ) : (
                  <MagnifyingGlassIcon size={20} className="text-primary-500 shrink-0" />
                )
              }
              afterInputDecorator={afterInputDecorator ? afterInputDecorator : null}
              placeholder={placeholder}
              searchElements={suggestions.map((suggestion) => ({
                label: suggestion.description,
                value: suggestion.place_id,
              }))}
              inputValue={field.value?.address ?? ""}
              onChange={(element) => handleChange(element, field)}
              isError={!!fieldState.error}
              disabled={disabled}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

export default FormSearchAddress;
